import { Link } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "convex/_generated/api";
import { useUser } from "../hooks/useUser";
import { SessionList } from "../components/SessionList";

export function HomePage() {
  const { user, isLoading } = useUser();
  const { signOut } = useAuthActions();
  const sessions = useQuery(api.sessions.listMySessions, {
    includeCompleted: false,
  });
  const isAdmin = useQuery(api.users.isAdmin);
  const updateProfile = useMutation(api.users.updateProfile);

  const handleDisplayNameChange = async (newName: string) => {
    try {
      await updateProfile({ displayName: newName });
    } catch (error) {
      console.error("Failed to update display name:", error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // Auth is handled by ProtectedRoute wrapper - user is guaranteed to be authenticated here
  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-center mb-2">
          Collaborative Crossword
        </h1>
        <p className="text-gray-600 text-center mb-12">
          Solve crossword puzzles together with friends in real-time
        </p>

        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-lg font-semibold">Your Profile</h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
              Authenticated
            </span>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600 w-32">Display Name:</label>
              <input
                type="text"
                value={user.displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {user.email && (
              <div className="flex items-center gap-4">
                <label className="text-sm text-gray-600 w-32">Email:</label>
                <span className="text-gray-900">{user.email}</span>
                {user.emailVerified && (
                  <span className="text-xs text-green-600">✓ Verified</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sessions List */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Sessions</h2>
          {sessions === undefined ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <SessionList sessions={sessions} />
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4">
          <Link
            to="/puzzles"
            className="block w-full py-4 px-6 bg-blue-600 text-white text-center rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start New Game
          </Link>

          <button
            onClick={handleSignOut}
            className="block w-full py-2 px-4 text-center text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Sign Out
          </button>

          {isAdmin && (
            <Link
              to="/admin"
              className="block w-full py-2 px-4 text-center text-gray-600 hover:text-gray-800 text-sm"
            >
              Admin Panel
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
